import React, { useCallback, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";
import {
  getClientGroups,
  deleteClientGroup,
  getClients,
  deleteClient,
  CLIENTS_PAGE_SIZE,
} from "features/Clients/api";
import { ClientGroupModal } from "features/Clients/ClientGroupModal";
import { ClientModal } from "features/Clients/ClientModal";
import { ChangeGroupModal } from "features/Clients/ChangeGroupModal";
import { AccrueBonusesModal } from "features/Clients/AccrueBonusesModal";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText, RegularText } from "components/Text";
import { Button } from "components/Button";
import { Table } from "components/Table";
import { SearchInput } from "components/SearchInput";
import { SimpleList, SimpleListRow } from "components/SimpleList";
import { useModal, ConfirmationModal, InfoModal } from "components/Modal";

import styles from "./styles.module.scss";

const getClientDisplayName = (client) =>
  Number(client.firma) === 0
    ? `${client.firstname ?? ""} ${client.secondname ?? ""}`.trim()
    : client.firstname;

const GroupsPanel = ({ activeGroup, onSelectGroup }) => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const groupsQuery = useQuery(["client-groups"], getClientGroups);
  const groups = groupsQuery.data?.items ?? [];

  const openGroupModal = (isEdit) =>
    openModal(<ClientGroupModal isEdit={isEdit} group={activeGroup} />);

  const onDeleteClick = () => {
    if (!activeGroup) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить группу "${activeGroup.name}"?`}
        onConfirm={async () => {
          try {
            await deleteClientGroup(activeGroup.clientgroupid);
            queryClient.invalidateQueries(["client-groups"]);
            onSelectGroup(null);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <PageLayout
      className={styles.leftPanel}
      header={<BoldText>Группа клиентов</BoldText>}
      footer={
        <ActionsRow>
          <Button icon="plus" onClick={() => openGroupModal(false)}>
            Добавить группу
          </Button>
          <Button
            icon="delete"
            onClick={onDeleteClick}
            disabled={!activeGroup}
            title="Удалить группу"
          />
          <Button
            icon="info"
            onClick={() => openGroupModal(true)}
            disabled={!activeGroup}
            title="Свойства группы"
          />
        </ActionsRow>
      }
    >
      <SimpleList
        items={groups}
        rowKey="clientgroupid"
        activeId={activeGroup?.clientgroupid}
        loading={groupsQuery.isLoading}
        error={groupsQuery.isError ? getErrorMessage(groupsQuery.error) : ""}
        onRetry={groupsQuery.refetch}
        topRow={
          <SimpleListRow
            label="Все"
            isActive={!activeGroup}
            onClick={() => onSelectGroup(null)}
          />
        }
        onRowClick={onSelectGroup}
        onRowOpen={() => openGroupModal(true)}
      />
    </PageLayout>
  );
};

const ClientsPanel = ({ activeGroup }) => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const [search, setSearch] = useState("");
  const [checkedIds, setCheckedIds] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const groupId = activeGroup?.clientgroupid;

  const clientsQuery = useInfiniteQuery(
    ["clients", groupId, search],
    ({ pageParam = 1 }) =>
      getClients({ page: pageParam, clientgroupid: groupId, search }),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.items.length < CLIENTS_PAGE_SIZE
          ? undefined
          : allPages.length + 1,
      keepPreviousData: false,
    }
  );

  const clients = useMemo(
    () => (clientsQuery.data?.pages ?? []).flatMap((page) => page.items),
    [clientsQuery.data]
  );

  const checkedClients = useMemo(
    () => clients.filter((c) => checkedIds.includes(c.clientid)),
    [clients, checkedIds]
  );

  const clientsRef = useRef(clients);
  clientsRef.current = clients;

  const checkedIdsRef = useRef(checkedIds);
  checkedIdsRef.current = checkedIds;

  const rowData = useMemo(
    () =>
      clients.map((client) => ({
        clientid: client.clientid,
        name: getClientDisplayName(client),
        phone: client.phone,
        percent:
          client.percent == null || client.percent === ""
            ? null
            : Number(client.percent),
      })),
    [clients]
  );

  const toggleChecked = useCallback((clientid) => {
    const prev = checkedIdsRef.current;
    const next = prev.includes(clientid)
      ? prev.filter((id) => id !== clientid)
      : [...prev, clientid];
    checkedIdsRef.current = next;
    setCheckedIds(next);
  }, []);

  const formatPercent = useCallback((value) => {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    const text =
      Math.abs(n - Math.round(n)) < 1e-9
        ? String(Math.round(n))
        : String(n)
            .replace(/(\.\d*?)0+$/, "$1")
            .replace(/\.$/, "");
    return `${text} %`;
  }, []);

  const colDefs = useMemo(() => {
    // Vanilla-рендерер ag-grid (не React): клик не перерисовывает всю таблицу
    class ClientCheckboxRenderer {
      init(params) {
        this.params = params;
        this.eGui = document.createElement("input");
        this.eGui.type = "checkbox";
        this.eGui.className = styles.rowCheckbox;
        this.eGui.checked = checkedIdsRef.current.includes(
          params.data.clientid
        );
        this.eGui.addEventListener("click", (ev) => ev.stopPropagation());
        this.eGui.addEventListener("dblclick", (ev) => ev.stopPropagation());
        this.eGui.addEventListener("change", () => {
          toggleChecked(params.data.clientid);
        });
      }

      getGui() {
        return this.eGui;
      }

      refresh(params) {
        this.eGui.checked = checkedIdsRef.current.includes(
          params.data.clientid
        );
        return true;
      }
    }

    return [
      {
        colId: "checked",
        headerName: "",
        width: 50,
        sortable: false,
        cellClass: styles.checkboxCell,
        cellRenderer: ClientCheckboxRenderer,
      },
      { field: "name", headerName: "ФИО/Наименование", flex: 2, minWidth: 300 },
      { field: "phone", headerName: "Телефон", width: 130 },
      {
        field: "percent",
        headerName: "Процент скидки",
        width: 150,
        type: "rightAligned",
        comparator: (a, b) => {
          const na = a == null || a === "" ? Number.NEGATIVE_INFINITY : Number(a);
          const nb = b == null || b === "" ? Number.NEGATIVE_INFINITY : Number(b);
          return na - nb;
        },
        valueFormatter: (params) => formatPercent(params.value),
      },
    ];
  }, [toggleChecked, formatPercent]);

  const handleBodyScrollEnd = useCallback(
    (event) => {
      if (!clientsQuery.hasNextPage || clientsQuery.isFetchingNextPage) return;
      const lastVisible = event.api.getLastDisplayedRowIndex
        ? event.api.getLastDisplayedRowIndex()
        : event.api.getLastDisplayedRow?.();
      if (
        typeof lastVisible === "number" &&
        lastVisible >= rowData.length - 30
      ) {
        clientsQuery.fetchNextPage();
      }
    },
    [clientsQuery, rowData.length]
  );

  const openClientModal = useCallback(
    (isEdit, client) => {
      openModal(
        <ClientModal isEdit={isEdit} client={client} group={activeGroup} />
      );
    },
    [openModal, activeGroup]
  );

  const handleSelect = useCallback((row) => {
    setSelectedClient(
      clientsRef.current.find((c) => c.clientid === row.clientid) || null
    );
  }, []);

  const handleOpen = useCallback(
    (row) => {
      const client = clientsRef.current.find(
        (c) => c.clientid === row.clientid
      );
      if (client) {
        setSelectedClient(client);
        openClientModal(true, client);
      }
    },
    [openClientModal]
  );

  const tableError = clientsQuery.isError
    ? getErrorMessage(clientsQuery.error)
    : "";

  const onAddClick = () => {
    if (!activeGroup) {
      openModal(
        <InfoModal
          title="Выберите группу, в которой хотите создать клиента"
          onClose={closeModal}
        />
      );
      return;
    }
    openClientModal(false, null);
  };

  const onDeleteClick = () => {
    if (!checkedClients.length) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить выбранных клиентов (${checkedClients.length})?`}
        onConfirm={async () => {
          try {
            await Promise.all(
              checkedClients.map((c) => deleteClient(c.clientid))
            );
            setCheckedIds([]);
            queryClient.invalidateQueries(["clients"]);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  const onMoveClick = () => {
    if (!checkedClients.length) {
      openModal(
        <InfoModal
          title="Выберите клиентов для переноса"
          onClose={closeModal}
        />
      );
      return;
    }
    openModal(
      <ChangeGroupModal
        clients={checkedClients}
        currentGroup={activeGroup}
        onDone={() => setCheckedIds([])}
      />
    );
  };

  const onAccrueClick = () => {
    if (!checkedClients.length) return;
    openModal(
      <AccrueBonusesModal
        clients={checkedClients}
        onDone={() => setCheckedIds([])}
      />
    );
  };

  return (
    <PageLayout
      className={styles.rightPanel}
      header={<BoldText>Клиенты</BoldText>}
      footer={
        <ActionsRow>
          <Button icon="plus" onClick={onAddClick} title="Добавить клиента" />
          <Button
            icon="delete"
            onClick={onDeleteClick}
            disabled={!checkedClients.length}
            title="Удалить выбранных"
          />
          <Button
            icon="move"
            onClick={onMoveClick}
            disabled={!checkedClients.length}
            title="Перенести в другую группу"
          />
          <Button
            icon="coins"
            onClick={onAccrueClick}
            disabled={!checkedClients.length}
          >
            Начислить бонусы массово
          </Button>
        </ActionsRow>
      }
      bodyFill
    >
      <div className={styles.searchRow}>
        <SearchInput value={search} onChange={setSearch} placeholder="Поиск" />
      </div>
      <Table
        fill
        rowData={rowData}
        colDefs={colDefs}
        rowKey="clientid"
        selectedId={selectedClient?.clientid}
        onSelect={handleSelect}
        onOpen={handleOpen}
        loading={clientsQuery.isLoading}
        error={tableError}
        onRetry={clientsQuery.refetch}
        onBodyScrollEnd={handleBodyScrollEnd}
      />
      {clientsQuery.isFetchingNextPage && (
        <RegularText size={13} color="muted" className={styles.loadingMore}>
          Загрузка...
        </RegularText>
      )}
    </PageLayout>
  );
};

export const ClientsPage = () => {
  const [activeGroup, setActiveGroup] = useState(null);

  return (
    <div className={styles.page}>
      <GroupsPanel activeGroup={activeGroup} onSelectGroup={setActiveGroup} />
      <ClientsPanel activeGroup={activeGroup} />
    </div>
  );
};
